import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCompanySettings } from '../hooks/useCompanySettings';
import PreLoader from '../components/PreLoader';
import ScrollToTop from '../components/ScrollToTop';
import BreadcrumbNav from '../components/seo/BreadcrumbNav';
import SEO from '../components/SEO';
import { 
  FaHome, 
  FaHandshake, 
  FaShieldAlt, 
  FaChartLine, 
  FaUsers, 
  FaAward,
  FaBuilding,
  FaEye,
  FaBullseye,
  FaHeart,
  FaStar,
  FaCheckCircle,
  FaLightbulb,
  FaRocket,
  FaGem,
  FaThumbsUp,
  FaLock,
  FaWifi,
  FaTools,
  FaGraduationCap
} from 'react-icons/fa';
import fallbackAboutImage from '../back.jpg';
import CtaBanner from '../components/CtaBanner';

const DEFAULT_ABOUT_STATS = [
  { key: '500+', number: '500+', label: 'Imóveis Vendidos' },
  { key: '1000+', number: '1000+', label: 'Clientes Satisfeitos' },
  { key: '15+', number: '15+', label: 'Anos de Experiência' },
  { key: '98%', number: '98%', label: 'Taxa de Satisfação' }
];

function About() {
  const { 
    companyName,
    aboutTitle,
    aboutDescription,
    about,
    // Configurações Sobre Nós
    aboutHeroTitle,
    aboutHeroDescription,
    aboutHeroImage,
    aboutFeaturesTitle,
    aboutFeaturesSubtitle,
    aboutFeatures,
    aboutStat1Name,
    aboutStat1Value,
    aboutStat2Name,
    aboutStat2Value,
    aboutStat3Name,
    aboutStat3Value,
    aboutStat4Name,
    aboutStat4Value,
    aboutVisionText,
    aboutVisionImage,
    aboutMissionText,
    aboutMissionImage,
    aboutValuesText,
    aboutValuesImage,
    aboutHeroInsight1,
    aboutHeroInsight2,
    ctaAboutEnabled,
    ctaLayout,
    ctaTitle,
    ctaDescription,
    ctaButtonText,
    ctaImageSource,
    ctaSystemImage,
    ctaImageStyle,
    ctaImage,
    primaryColor,
    settingsReady,
  } = useCompanySettings();

  // Estados para animações de scroll e contadores
  const [isVisible, setIsVisible] = useState({
    hero: false,
    features: false,
    stats: false,
    vision: false,
    mission: false,
    values: false
  });
  const [counters, setCounters] = useState({});
  const [isPreLoading, setIsPreLoading] = useState(() => !settingsReady);
  
  const sectionRefs = useRef({});
  const counterRef = useRef(null);

  // Função para finalizar o pre-loading
  const handlePreLoadingComplete = useCallback(() => {
    setIsPreLoading(false);
  }, []);

  useEffect(() => {
    if (settingsReady) {
      setIsPreLoading(false);
    }
  }, [settingsReady]);

  // Hook para detectar elementos visíveis no scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    // Observar todas as seções
    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Animação dos contadores
  useEffect(() => {
    if (isVisible.stats && counterRef.current) {
      // Criar array de stats dinâmicos
      const dynamicStats = [];
      if (aboutStat1Name && aboutStat1Value) dynamicStats.push({ name: aboutStat1Name, value: aboutStat1Value });
      if (aboutStat2Name && aboutStat2Value) dynamicStats.push({ name: aboutStat2Name, value: aboutStat2Value });
      if (aboutStat3Name && aboutStat3Value) dynamicStats.push({ name: aboutStat3Name, value: aboutStat3Value });
      if (aboutStat4Name && aboutStat4Value) dynamicStats.push({ name: aboutStat4Name, value: aboutStat4Value });
      
      const statsToUse = dynamicStats.length > 0 ? dynamicStats : DEFAULT_ABOUT_STATS;
      
      // Criar targetValues baseado nos stats disponíveis
      const targetValues = {};
      statsToUse.forEach((stat, index) => {
        const key = `stat_${index}`;
        let targetValue = 0;
        
        if (stat.value) {
          // Extrair número do valor (ex: "500+" -> 500, "98%" -> 98)
          const numericValue = stat.value.replace(/[^\d]/g, '');
          targetValue = parseInt(numericValue) || 0;
        } else if (stat.key) {
          // Para stats padrão
          const numericValue = stat.key.replace(/[^\d]/g, '');
          targetValue = parseInt(numericValue) || 0;
        }
        
        targetValues[key] = targetValue;
      });
      
      const duration = 2000; // 2 segundos
      const steps = 60; // 60 FPS
      const stepDuration = duration / steps;
      
      const animateCounter = (key, target) => {
        let current = 0;
        const increment = target / steps;
        
        const timer = setInterval(() => {
          current += increment;
          if (current >= target) {
            current = target;
            clearInterval(timer);
          }
          
          setCounters(prev => ({
            ...prev,
            [key]: Math.floor(current)
          }));
        }, stepDuration);
      };

      // Animar cada contador com delay
      Object.entries(targetValues).forEach(([key, target], index) => {
        setTimeout(() => {
          animateCounter(key, target);
        }, index * 200);
      });
    }
  }, [isVisible.stats, aboutStat1Name, aboutStat1Value, aboutStat2Name, aboutStat2Value, aboutStat3Name, aboutStat3Value, aboutStat4Name, aboutStat4Value]);

  // Parallax leve com rAF (evita trabalho em todo scroll event)
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrolled = window.pageYOffset;
        document.querySelectorAll('.parallax-slow').forEach((element) => {
          const speed = Number(element.dataset.speed) || 0.5;
          element.style.transform = `translateY(${-(scrolled * speed)}px)`;
        });
        ticking = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Dados padrão caso não estejam configurados
  const defaultValues = {
    title: aboutTitle || (companyName ? `Sobre a ${companyName}` : 'Sobre Nós'),
    description: aboutDescription || `
      <p>Somos uma empresa imobiliária comprometida em transformar sonhos em realidade. Com anos de experiência no mercado, oferecemos soluções completas em compra, venda e locação de imóveis.</p>
      
      <p>Nossa missão é proporcionar um atendimento personalizado e transparente, sempre priorizando as necessidades e expectativas de nossos clientes. Acreditamos que cada negócio imobiliário é único e merece atenção especial.</p>
      
      <p>Com uma equipe de profissionais qualificados e experientes, utilizamos as mais modernas tecnologias e ferramentas para garantir que você encontre o imóvel perfeito ou realize a venda ideal.</p>
    `,
    image: about || fallbackAboutImage
  };

  const features = [
    {
      icon: FaHome,
      title: 'Propriedades Diversas',
      description: 'Amplo portfólio de imóveis residenciais e comerciais em diversas localizações.'
    },
    {
      icon: FaHandshake,
      title: 'Atendimento Personalizado',
      description: 'Cada cliente recebe atenção individualizada para suas necessidades específicas.'
    },
    {
      icon: FaShieldAlt,
      title: 'Segurança e Confiança',
      description: 'Transparência total em todas as negociações e documentação adequada.'
    },
    {
      icon: FaChartLine,
      title: 'Experiência Comprovada',
      description: 'Anos de experiência no mercado imobiliário com resultados satisfatórios.'
    },
    {
      icon: FaUsers,
      title: 'Equipe Qualificada',
      description: 'Profissionais especializados e atualizados com as melhores práticas do setor.'
    },
    {
      icon: FaAward,
      title: 'Qualidade Garantida',
      description: 'Compromisso com a excelência em todos os nossos serviços e atendimentos.'
    }
  ];

  const aboutBreadcrumbs = [
    { name: 'Início', path: '/' },
    { name: 'Sobre Nós', current: true },
  ];

  return (
    <>
      {/* PreLoader Component */}
      <PreLoader 
        isLoading={isPreLoading} 
        onLoadingComplete={handlePreLoadingComplete}
      />
      
      <SEO
        title={`Sobre Nós - ${companyName || 'Imobiliária'}`}
        description={`Conheça mais sobre ${companyName ? `a ${companyName}` : 'nossa imobiliária'} - nossa história, missão e compromisso com a excelência no mercado imobiliário.`}
        keywords={`sobre nós, imobiliária, história, missão, valores${companyName ? `, ${companyName}` : ''}`}
        ogTitle={`Sobre Nós - ${companyName || 'Imobiliária'}`}
        ogDescription={`Conheça mais sobre ${companyName ? `a ${companyName}` : 'nossa imobiliária'} - nossa história, missão e compromisso com a excelência no mercado imobiliário.`}
        canonicalUrl="/sobre"
        pageType="website"
        includeSiteSchemas
      />

      <ScrollToTop />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section 
          id="hero"
          ref={el => sectionRefs.current.hero = el}
          className={`relative bg-white text-gray-900 overflow-hidden transition-all duration-1000 ${isVisible.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {/* Breadcrumbs */}
          <div className="relative z-40 pt-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="mb-8">
                <BreadcrumbNav items={aboutBreadcrumbs} />
              </div>
            </div>
          </div>
          {/* Floating Building Icons */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-10 w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center animate-bounce" style={{ animationDelay: '0s', animationDuration: '3s' }}>
              <FaHome className="w-10 h-10 text-gray-500" />
            </div>
            <div className="absolute top-40 right-20 w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center animate-bounce" style={{ animationDelay: '1s', animationDuration: '4s' }}>
              <FaBuilding className="w-8 h-8 text-gray-500" />
            </div>
            <div className="absolute bottom-40 left-1/4 w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center animate-bounce" style={{ animationDelay: '2s', animationDuration: '5s' }}>
              <FaHome className="w-6 h-6 text-gray-500" />
            </div>
            <div className="absolute bottom-10 right-1/3 w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center animate-bounce" style={{ animationDelay: '1.5s', animationDuration: '3.5s' }}>
              <FaBuilding className="w-12 h-12 text-gray-500" />
            </div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div className="animate-fade-in-up">
                  <h1 className="text-4xl lg:text-5xl font-bold leading-tight text-gray-900 typewriter">
                    {aboutHeroTitle || defaultValues.title}
                  </h1>
                </div>
                
                <div 
                  className="text-lg leading-relaxed text-gray-600 space-y-4 animate-fade-in-up opacity-0"
                  style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}
                  dangerouslySetInnerHTML={{ __html: aboutHeroDescription || defaultValues.description }}
                />
                
                <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up opacity-0" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}>
                  <a 
                    href="/propriedades"
                    className="group inline-flex items-center justify-center px-8 py-4 border-2 font-semibold rounded-lg transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 shadow-lg hover:shadow-xl"
                    style={{
                      borderColor: 'var(--primary-color, #3B82F6)',
                      backgroundColor: 'var(--primary-color, #3B82F6)',
                      color: 'var(--secondary-color, #10B981)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'var(--secondary-color, #10B981)';
                      e.target.style.color = 'var(--primary-color, #3B82F6)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'var(--primary-color, #3B82F6)';
                      e.target.style.color = 'var(--secondary-color, #10B981)';
                    }}
                  >
                    <span className="relative z-10">Ver Propriedades</span>
                    <svg className="ml-2 w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </a>
                  <a 
                    href="/contato"
                    className="group inline-flex items-center justify-center px-8 py-4 border-2 font-semibold rounded-lg transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 shadow-lg hover:shadow-xl"
                    style={{
                      borderColor: 'var(--primary-color, #3B82F6)',
                      backgroundColor: 'var(--secondary-color, #10B981)',
                      color: 'var(--primary-color, #3B82F6)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'var(--primary-color, #3B82F6)';
                      e.target.style.color = 'var(--secondary-color, #10B981)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'var(--secondary-color, #10B981)';
                      e.target.style.color = 'var(--primary-color, #3B82F6)';
                    }}
                  >
                    <span className="relative z-10">Entre em Contato</span>
                  </a>
                </div>
              </div>
              
              <div className="relative animate-fade-in-up opacity-0" style={{ animationDelay: '0.9s', animationFillMode: 'forwards' }}>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-all duration-700">
                  <img 
                    src={aboutHeroImage || defaultValues.image} 
                    alt="Sobre Nós"
                    width={800}
                    height={500}
                    decoding="async"
                    fetchPriority="high"
                    className="w-full h-96 lg:h-[500px] object-cover transition-transform duration-700 hover:scale-110"
                  />
                </div>
                
                {/* Floating Cards with Dynamic Insights */}
                {(() => {
                  // Função para obter dados do insight selecionado
                  const getInsightData = (insightId) => {
                    if (!insightId) return null;
                    
                    const insights = [
                      { id: 'stat1', name: aboutStat1Name, value: aboutStat1Value },
                      { id: 'stat2', name: aboutStat2Name, value: aboutStat2Value },
                      { id: 'stat3', name: aboutStat3Name, value: aboutStat3Value },
                      { id: 'stat4', name: aboutStat4Name, value: aboutStat4Value }
                    ];
                    
                    return insights.find(insight => insight.id === insightId);
                  };
                  
                  // Função para obter ícone baseado no nome do insight
                  const getInsightIcon = (name) => {
                    if (!name) return FaAward;
                    
                    const nameLower = name.toLowerCase();
                    if (nameLower.includes('cliente') || nameLower.includes('satisfeito')) return FaUsers;
                    if (nameLower.includes('ano') || nameLower.includes('experiência')) return FaAward;
                    if (nameLower.includes('imóvel') || nameLower.includes('propriedade')) return FaHome;
                    if (nameLower.includes('venda') || nameLower.includes('vendas')) return FaChartLine;
                    return FaAward;
                  };
                  
                  const insight1 = getInsightData(aboutHeroInsight1);
                  const insight2 = getInsightData(aboutHeroInsight2);
                  
                  return (
                    <>
                      {insight1 && (
                        <div className="absolute bottom-2 left-2 lg:-bottom-6 lg:-left-6 bg-white rounded-lg p-2 lg:p-4 shadow-xl transform hover:scale-110 hover:-rotate-2 transition-all duration-500 animate-float">
                          <div className="flex items-center space-x-2 lg:space-x-4">
                            <div className="p-2 lg:p-3 bg-blue-100 rounded-lg">
                              {React.createElement(getInsightIcon(insight1.name), { className: "w-4 h-4 lg:w-6 lg:h-6 text-blue-400" })}
                            </div>
                            <div>
                              <p className="text-base lg:text-2xl font-bold text-gray-900 animate-count-up">{insight1.value}</p>
                              <p className="text-xs lg:text-sm text-gray-600 whitespace-nowrap">{insight1.name}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {insight2 && (
                        <div className="absolute top-2 right-2 lg:-top-6 lg:-right-6 bg-white rounded-lg p-2 lg:p-4 shadow-xl transform hover:scale-110 hover:rotate-2 transition-all duration-500 animate-float-delayed">
                          <div className="flex items-center space-x-2 lg:space-x-4">
                            <div className="p-2 lg:p-3 bg-green-100 rounded-lg animate-pulse">
                              {React.createElement(getInsightIcon(insight2.name), { className: "w-4 h-4 lg:w-6 lg:h-6 text-green-600" })}
                            </div>
                            <div>
                              <p className="text-base lg:text-2xl font-bold text-gray-900 animate-count-up-delayed">{insight2.value}</p>
                              <p className="text-xs lg:text-sm text-gray-600 whitespace-nowrap">{insight2.name}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section 
          id="features"
          ref={el => sectionRefs.current.features = el}
          className={`py-20 bg-gray-50 relative overflow-hidden transition-all duration-1000 ${isVisible.features ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-0 w-72 h-72 bg-blue-100/30 rounded-full blur-3xl animate-pulse parallax-slow" data-speed="0.2"></div>
            <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl animate-pulse parallax-slow" data-speed="0.4" style={{ animationDelay: '1s' }}></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16 animate-fade-in-up">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                {aboutFeaturesTitle || (companyName ? `Por Que Escolher a ${companyName}?` : 'Por Que Nos Escolher?')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up opacity-0" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
                {aboutFeaturesSubtitle || 'Nossos diferenciais que fazem a diferença na sua experiência imobiliária'}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {(aboutFeatures && aboutFeatures.length > 0 ? aboutFeatures : features).map((feature, index) => {
                // Mapear ícones para features dinâmicos
                const getIconComponent = (iconName) => {
                  const iconMap = {
                    'FaHandshake': FaHandshake,
                    'FaShieldAlt': FaShieldAlt,
                    'FaChartLine': FaChartLine,
                    'FaUsers': FaUsers,
                    'FaAward': FaAward,
                    'FaHome': FaHome,
                    'FaBuilding': FaBuilding,
                    'FaEye': FaEye,
                    'FaBullseye': FaBullseye,
                    'FaHeart': FaHeart,
                    'FaStar': FaStar,
                    'FaCheckCircle': FaCheckCircle,
                    'FaLightbulb': FaLightbulb,
                    'FaRocket': FaRocket,
                    'FaGem': FaGem,
                    'FaThumbsUp': FaThumbsUp,
                    'FaLock': FaLock,
                    'FaWifi': FaWifi,
                    'FaTools': FaTools,
                    'FaGraduationCap': FaGraduationCap
                  };
                  return iconMap[iconName] || FaAward;
                };
                
                const IconComponent = getIconComponent(feature.icon || feature.iconName);
                return (
                  <div 
                    key={index}
                    className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-4 hover:rotate-1 group relative overflow-hidden ${isVisible.features ? 'scale-100 translate-y-0 opacity-100' : 'scale-75 translate-y-8 opacity-0'}`}
                    style={{ transitionDelay: `${index * 0.2}s` }}
                  >
                    {/* Card Background Effect */}
                    <div 
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ 
                        background: `linear-gradient(to bottom right, var(--primary-color, #3B82F6)/0.5, transparent)`
                      }}
                    ></div>
                    <div 
                      className="absolute top-0 right-0 w-32 h-32 rounded-full blur-2xl transform translate-x-16 -translate-y-16 group-hover:scale-150 transition-transform duration-300"
                      style={{ 
                        backgroundColor: `var(--primary-color, #3B82F6)/0.2`
                      }}
                    ></div>
                    
                      <div className="relative z-10">
                        <div className="inline-flex items-center justify-center w-16 h-16 mb-6 transition-all duration-300 transform group-hover:scale-110 group-hover:rotate-12">
                          <IconComponent 
                            className="w-8 h-8 transition-all duration-300 transform group-hover:scale-110"
                            style={{ 
                              color: 'var(--primary-color, #3B82F6)'
                            }}
                          />
                        </div>
                      <h3 
                        className="text-xl font-semibold text-gray-900 mb-4 transition-colors duration-300"
                        style={{
                          color: 'inherit'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = 'var(--primary-color, #3B82F6)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = 'inherit';
                        }}
                      >
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                        {feature.description}
                      </p>
                      
                      {/* Hover Effect Line */}
                      <div 
                        className="mt-6 w-0 group-hover:w-full h-1 transition-all duration-300 rounded-full"
                        style={{
                          background: `linear-gradient(to right, var(--primary-color, #3B82F6), var(--primary-color-hover, #2563EB))`
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section 
          id="stats"
          ref={el => {
            sectionRefs.current.stats = el;
            counterRef.current = el;
          }}
          className={`py-16 bg-white relative overflow-hidden transition-all duration-1000 ${isVisible.stats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {/* Background Effects */}
          <div className="absolute inset-0 opacity-30" style={{ background: `linear-gradient(to right, var(--primary-color, #3B82F6)/0.3, transparent, var(--primary-color, #3B82F6)/0.3)` }}></div>
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-96 h-96 rounded-full blur-3xl parallax-slow" style={{ backgroundColor: `var(--primary-color, #3B82F6)/0.2` }} data-speed="0.3"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {(() => {
              // Criar array de stats dinâmicos
              const dynamicStats = [];
              if (aboutStat1Name && aboutStat1Value) dynamicStats.push({ name: aboutStat1Name, value: aboutStat1Value });
              if (aboutStat2Name && aboutStat2Value) dynamicStats.push({ name: aboutStat2Name, value: aboutStat2Value });
              if (aboutStat3Name && aboutStat3Value) dynamicStats.push({ name: aboutStat3Name, value: aboutStat3Value });
              if (aboutStat4Name && aboutStat4Value) dynamicStats.push({ name: aboutStat4Name, value: aboutStat4Value });
              
              const statsToUse = dynamicStats.length > 0 ? dynamicStats : DEFAULT_ABOUT_STATS;
              const statsCount = statsToUse.length;
              
              // Definir classes responsivas baseadas na quantidade de stats
              let gridClasses = '';
              if (statsCount === 1) {
                gridClasses = 'grid-cols-1 max-w-md mx-auto';
              } else if (statsCount === 2) {
                gridClasses = 'grid-cols-1 md:grid-cols-2';
              } else if (statsCount === 3) {
                gridClasses = 'grid-cols-1 md:grid-cols-3';
              } else {
                gridClasses = 'grid-cols-2 md:grid-cols-4';
              }
              
              return (
                <div className={`grid ${gridClasses} gap-8`}>
                  {statsToUse.map((stat, index) => (
                <div 
                  key={index} 
                  className={`text-center group transform transition-all duration-1000 ${isVisible.stats ? 'scale-100 translate-y-0 opacity-100' : 'scale-75 translate-y-8 opacity-0'}`}
                  style={{ transitionDelay: `${index * 0.2}s` }}
                >
                  <div className="relative">
                    <div 
                      className={`text-4xl lg:text-5xl font-bold mb-2 transition-colors duration-300 ${isVisible.stats ? 'counter-animate' : ''}`}
                      style={{ 
                        color: 'var(--primary-color, #3B82F6)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = 'var(--primary-color-hover, #2563EB)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = 'var(--primary-color, #3B82F6)';
                      }}
                    >
                      {(() => {
                        const counterKey = `stat_${index}`;
                        const currentCounter = counters[counterKey] || 0;
                        
                        if (stat.value) {
                          // Para stats dinâmicos, manter o formato original mas com animação
                          const suffix = stat.value.replace(/[\d]/g, '');
                          return `${currentCounter}${suffix}`;
                        } else if (stat.key) {
                          // Para stats padrão
                          return stat.key === '98%' ? `${currentCounter}%` : `${currentCounter}+`;
                        }
                        return stat.number || '0';
                      })()}
                    </div>
                    <div 
                      className="absolute inset-0 text-4xl lg:text-5xl font-bold mb-2 opacity-0 group-hover:opacity-20 blur-sm transition-all duration-300"
                      style={{ color: 'var(--primary-color, #3B82F6)' }}
                    >
                      {stat.value || stat.number}
                    </div>
                  </div>
                  <div className="text-gray-600 font-medium group-hover:text-gray-800 transition-colors duration-300">
                    {stat.name || stat.label}
                  </div>
                  <div 
                    className={`mt-2 h-0.5 transition-all duration-1000 mx-auto ${isVisible.stats ? 'w-full' : 'w-0'}`}
                    style={{ 
                      background: `linear-gradient(to right, var(--primary-color, #3B82F6), var(--primary-color-hover, #2563EB))`,
                      transitionDelay: `${index * 0.2 + 0.5}s`
                    }}
                  ></div>
                </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </section>

        {/* Vision Section */}
        <section 
          id="vision"
          ref={el => sectionRefs.current.vision = el}
          className={`py-20 bg-gray-50 transition-all duration-1000 ${isVisible.vision ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* IMAGEM | TEXTO */}
              <div className="relative">
                {aboutVisionImage ? (
                  <img 
                    src={aboutVisionImage} 
                    alt="Nossa Visão"
                    width={640}
                    height={320}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-80 object-cover rounded-2xl transition-transform duration-700 hover:scale-105"
                  />
                ) : (
                  <div 
                    className="rounded-2xl p-8 h-80 flex items-center justify-center"
                    style={{ 
                      background: `linear-gradient(to bottom right, var(--primary-color, #3B82F6), var(--primary-color-hover, #2563EB))`
                    }}
                  >
                    <div className="text-center">
                      <FaEye 
                        className="w-16 h-16 mx-auto mb-4" 
                        style={{ color: 'var(--secondary-color, #10B981)' }}
                      />
                      <h3 
                        className="text-2xl font-bold"
                        style={{ color: 'var(--secondary-color, #10B981)' }}
                      >
                        Nossa Visão
                      </h3>
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  Nossa Visão
                </h2>
                <div 
                  className="text-lg text-gray-600 leading-relaxed"
                  dangerouslySetInnerHTML={{ 
                    __html: aboutVisionText || 'Ser reconhecida como a principal referência no mercado imobiliário local, conhecida pela excelência no atendimento, inovação tecnológica e resultados excepcionais que transformam sonhos em realidade.'
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section 
          id="mission"
          ref={el => sectionRefs.current.mission = el}
          className={`py-20 bg-white transition-all duration-1000 ${isVisible.mission ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* TEXTO | IMAGEM */}
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  Nossa Missão
                </h2>
                <div 
                  className="text-lg text-gray-600 leading-relaxed"
                  dangerouslySetInnerHTML={{ 
                    __html: aboutMissionText || 'Conectar pessoas aos seus sonhos imobiliários através de um serviço excepcional, transparência total e resultados que superam expectativas, construindo relacionamentos duradouros baseados na confiança e excelência.'
                  }}
                />
              </div>
              
              <div className="relative">
                {aboutMissionImage ? (
                  <img 
                    src={aboutMissionImage} 
                    alt="Nossa Missão"
                    width={640}
                    height={320}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-80 object-cover rounded-2xl transition-transform duration-700 hover:scale-105"
                  />
                ) : (
                  <div 
                    className="rounded-2xl p-8 h-80 flex items-center justify-center"
                    style={{ 
                      background: `linear-gradient(to bottom right, var(--primary-color, #3B82F6), var(--primary-color-hover, #2563EB))`
                    }}
                  >
                    <div className="text-center">
                      <FaBullseye 
                        className="w-16 h-16 mx-auto mb-4" 
                        style={{ color: 'var(--secondary-color, #10B981)' }}
                      />
                      <h3 
                        className="text-2xl font-bold"
                        style={{ color: 'var(--secondary-color, #10B981)' }}
                      >
                        Nossa Missão
                      </h3>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section 
          id="values"
          ref={el => sectionRefs.current.values = el}
          className={`py-20 bg-gray-50 transition-all duration-1000 ${isVisible.values ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* IMAGEM | TEXTO */}
              <div className="relative">
                {aboutValuesImage ? (
                  <img 
                    src={aboutValuesImage} 
                    alt="Nossos Valores"
                    width={640}
                    height={320}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-80 object-cover rounded-2xl transition-transform duration-700 hover:scale-105"
                  />
                ) : (
                  <div 
                    className="rounded-2xl p-8 h-80 flex items-center justify-center"
                    style={{ 
                      background: `linear-gradient(to bottom right, var(--primary-color, #3B82F6), var(--primary-color-hover, #2563EB))`
                    }}
                  >
                    <div className="text-center">
                      <FaHeart 
                        className="w-16 h-16 mx-auto mb-4" 
                        style={{ color: 'var(--secondary-color, #10B981)' }}
                      />
                      <h3 
                        className="text-2xl font-bold"
                        style={{ color: 'var(--secondary-color, #10B981)' }}
                      >
                        Nossos Valores
                      </h3>
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  Nossos Valores
                </h2>
                <div 
                  className="text-lg text-gray-600 leading-relaxed"
                  dangerouslySetInnerHTML={{ 
                    __html: aboutValuesText || 'Nossos valores fundamentais guiam cada decisão e ação em nossa empresa, criando uma base sólida para relacionamentos duradouros e resultados excepcionais.'
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        <CtaBanner
          enabled={ctaAboutEnabled}
          ctaLayout={ctaLayout}
          ctaTitle={ctaTitle}
          ctaDescription={ctaDescription}
          ctaButtonText={ctaButtonText}
          ctaImageSource={ctaImageSource}
          ctaSystemImage={ctaSystemImage}
          ctaImageStyle={ctaImageStyle}
          ctaImage={ctaImage}
          primaryColor={primaryColor}
        />
      </div>
      
      {/* Custom CSS Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes floatDelayed {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-15px);
          }
        }
        
        @keyframes spinSlow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes countUp {
          from {
            opacity: 0;
            transform: scale(0.5);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes countUpDelayed {
          from {
            opacity: 0;
            transform: scale(0.3) rotate(-10deg);
          }
          to {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: floatDelayed 4s ease-in-out infinite;
          animation-delay: 1s;
        }
        
        .animate-spin-slow {
          animation: spinSlow 8s linear infinite;
        }
        
        .animate-count-up {
          animation: countUp 1s ease-out forwards;
          animation-delay: 0.5s;
        }
        
        .animate-count-up-delayed {
          animation: countUpDelayed 1.2s ease-out forwards;
          animation-delay: 1s;
        }
        
        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }
        
        
        /* Scroll reveal animations */
        .scroll-reveal {
          opacity: 0;
          transform: translateY(50px);
          transition: all 0.8s ease-out;
        }
        
        .scroll-reveal.revealed {
          opacity: 1;
          transform: translateY(0);
        }
        
        /* Counter animation */
        @keyframes counterGlow {
          0% {
            text-shadow: 0 0 5px var(--primary-color, #3B82F6)/0.5;
          }
          50% {
            text-shadow: 0 0 20px var(--primary-color, #3B82F6)/0.8, 0 0 30px var(--primary-color, #3B82F6)/0.6;
          }
          100% {
            text-shadow: 0 0 5px var(--primary-color, #3B82F6)/0.5;
          }
        }
        
        .counter-animate {
          animation: counterGlow 2s ease-in-out;
        }
        
        /* Parallax effect for background elements */
        .parallax-slow {
          transform: translateY(0);
          transition: transform 0.1s ease-out;
        }
        
        /* Enhanced hover effects */
        .hover-lift {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .hover-lift:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }
        
        /* Staggered animation for grid items */
        .stagger-animation {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.6s ease-out;
        }
        
        .stagger-animation.animate {
          opacity: 1;
          transform: translateY(0);
        }
        
        /* Typewriter effect */
        @keyframes typewriter {
          from {
            clip-path: inset(0 100% 0 0);
          }
          to {
            clip-path: inset(0 0% 0 0);
          }
        }
        
        
        .typewriter {
          position: relative;
          animation: typewriter 3s ease-in-out 0.5s forwards;
          clip-path: inset(0 100% 0 0);
        }
      `}</style>
    </>
  );
}

export default About;

